import { useState } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { AlertTriangle } from "lucide-react";
import { userService } from "../../services/userService";

interface DeleteAdminDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  admin: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email: string;
  } | null;
}

export function DeleteAdminDialog({
  isOpen,
  onClose,
  onSuccess,
  admin,
}: DeleteAdminDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!admin) return;

    setIsLoading(true);
    setError(null);

    try {
      await userService.deleteUser(admin._id);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Delete admin error:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to delete admin. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!admin) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Admin">
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-4 bg-red-50 text-red-700 rounded-lg border border-red-100">
          <AlertTriangle size={24} className="shrink-0" />
          <p className="text-sm font-medium">
            Are you sure you want to delete this admin account? This action
            cannot be undone.
          </p>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
          <p className="text-sm text-gray-500 mb-1">Admin to delete:</p>
          <p className="font-semibold text-gray-900">
            {admin.firstName} {admin.lastName}
          </p>
          <p className="text-sm text-gray-600">{admin.email}</p>
        </div>

        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleDelete}
            disabled={isLoading}
            className="bg-red-600 hover:bg-red-700 border-red-600 hover:border-red-700 text-white"
          >
            {isLoading ? "Deleting..." : "Delete Admin"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
